<template>
  <div class="image-gallery">
    <div class="row row-imagen-principal position-relative">
      <template v-if="images.length === 1">
        <div class="col-12 h-100 p-0">
          <div class="image-wrapper h-100">
            <img :src="images[0].url" class="w-100 h-100 object-fit-cover" />
          </div>
        </div>
      </template>
      <template v-else-if="images.length === 2">
        <div v-for="(image, index) in images" :key="index" class="col-6 h-100">
          <div class="image-wrapper h-100 mr-12">
            <img :src="image.url" class="w-100 h-100 object-fit-cover" />
          </div>
        </div>
      </template>
      <template v-else-if="images.length >= 3">
        <div class="col-6 col-md-8 h-100 pe-0 ps-0">
          <div class="image-wrapper h-100 mr-12">
            <img :src="images[0].url" class="w-100 h-100 object-fit-cover" />
          </div>
        </div>
        <div class="col-6 col-md-4 h-100 px-0">
          <div class="d-flex flex-column h-100">
            <div class="image-wrapper mb-12 h-50">
              <img :src="images[1].url" class="w-100 h-100 object-fit-cover" />
            </div>
            <div class="image-wrapper h-50">
              <img :src="images[2].url" class="w-100 h-100 object-fit-cover" />
            </div>
          </div>
        </div>
      </template>
      <button
        v-if="images.length > 1"
        class="btn btn-light position-absolute shadow-sm d-flex align-items-center justify-content-center boton-ver-fotos"
        @click="openGallery">
        <CustomIcon :icon="imageIcon" size="20px" class="text-landing me-1 d-none d-md-inline-block" />
        <span class="p medium text-landing">{{ t('general.verFotos') }}</span>
      </button>
    </div>

    <Teleport to="body">
      <div v-if="showGallery" class="modal-gallery" @click="closeGallery">
        <div class="gallery-container">
          <button class="close-button" @click="closeGallery" @keyup.esc="closeGallery">×</button>
          <div class="carousel-wrapper">
            <Carousel :items-to-show="1" wrap-around :touch-drag="images.length > 1" :mouse-drag="images.length > 1">
              <Slide v-for="(image, index) in images" :key="index">
                <div class="d-flex justify-content-center align-items-center full-height">
                  <img :src="image.url" class="gallery-image" loading="lazy" />
                </div>
              </Slide>
              <template #addons>
                <Navigation />
              </template>
            </Carousel>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { Carousel, Slide, Navigation } from 'vue3-carousel';
  import imageIcon from '@/assets/icons/image.svg';
  import CustomIcon from '@/components/CustomIcon.vue';

  defineProps({
    images: {
      type: Array,
      default: () => [],
    },
    landing: {
      type: Boolean,
      default: false,
    },
  });

  const { t } = useI18n();
  const showGallery = ref(false);

  function openGallery() {
    document.body.style.overflow = 'hidden';
    showGallery.value = true;
  }

  function closeGallery(event) {
    const target = event?.target;
    if (target?.classList?.contains('gallery-image') || target?.closest('.carousel__prev, .carousel__next')) {
      return;
    }
    document.body.style.overflow = '';
    showGallery.value = false;
  }
</script>

<style scoped lang="scss">
  .image-gallery {
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;

    @media (max-width: 768px) {
      margin: 0 !important;
      padding: 0 !important;
    }
  }

  .row-imagen-principal {
    height: 420px;
    margin-left: 0;
    margin-right: 0;
    width: 100%;

    @media (max-width: 768px) {
      height: 200px;
    }
  }

  .row-imagen-principal > [class*='col-'] {
    padding-left: 0;
    padding-right: 0;
  }

  .image-wrapper {
    overflow: hidden;
    border-radius: 12px;
    position: relative;

    @media (max-width: 768px) {
      border-radius: 0px;
    }
  }

  .boton-ver-fotos {
    z-index: 2;
    bottom: 20px;
    right: 20px;
    height: 50px;
    width: 140px;
    border: none;
    background-color: #fff;
    background-image: linear-gradient(
      var(--landing-section-background-color, transparent),
      var(--landing-section-background-color, transparent)
    );
    transition: box-shadow 0.2s ease;
    border-radius: 12px;

    @media (max-width: 768px) {
      bottom: 10px;
      right: 10px;
      height: 31px;
      width: auto;
      padding: 0 12px;
      border-radius: 50px;
    }

    &:hover {
      box-shadow: 0 4px 8px rgba(var(--bs-primary-rgb), 0.1);
    }
  }

  .modal-gallery {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(var(--bs-primary-rgb), 0.4);
    z-index: 1050;
    display: flex;
    justify-content: center;
    align-items: center;
    animation: fadeIn 0.3s ease;
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .gallery-container {
    width: 90%;
    height: 90%;
    position: relative;
  }

  .carousel-wrapper {
    width: 100%;
    height: 100%;
  }

  .close-button {
    position: absolute;
    top: -40px;
    right: 0;
    font-size: 30px;
    color: white;
    background: transparent;
    border: none;
    cursor: pointer;
    z-index: 1060;
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.1);
    }
  }

  .gallery-image {
    max-width: 100%;
    max-height: 80vh;
    object-fit: contain;
  }

  .full-height {
    height: 80vh;
  }

  .modal-gallery {
    :deep(.carousel__prev),
    :deep(.carousel__next) {
      background-color: transparent;
      border: 2px solid white;
      border-radius: 50%;
      width: 40px;
      height: 40px;
      box-shadow: none;

      &:hover {
        background-color: rgba(var(--bs-primary-rgb), 0.1);
      }

      .carousel__icon {
        fill: #fff;
      }
    }
  }
</style>
